import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { GRID_COLUMNS } from '../../types/lawnTypes';

/**
 * Drag-and-resize widget grid.
 *
 * Each child is positioned from its `DashboardWidget` row (x, y, width, height)
 * on a 12-column grid, so the layout the user arranges is the same data the
 * `lawn_dashboard_widgets` table stores. Geometry changes are reported through
 * `onLayoutChange` once the pointer is released, which keeps persistence to one
 * write per gesture.
 */

const ROW_HEIGHT = 48;
const GRID_GAP = 12;
const MIN_WIDTH = 2;
const MIN_HEIGHT = 3;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export default function WidgetGrid({ widgets, renderWidget, onLayoutChange, onRemoveWidget }) {
  const containerRef = useRef(null);
  const [columnWidth, setColumnWidth] = useState(96);
  // Live geometry while dragging/resizing; null when idle.
  const [draft, setDraft] = useState(null);
  const gestureRef = useRef(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const measure = () => {
      const totalGap = GRID_GAP * (GRID_COLUMNS - 1);
      setColumnWidth((element.clientWidth - totalGap) / GRID_COLUMNS);
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const effectiveWidgets = useMemo(() => {
    if (!draft) return widgets;
    return widgets.map((widget) => (widget.id === draft.id ? { ...widget, ...draft } : widget));
  }, [widgets, draft]);

  const gridRows = useMemo(
    () => effectiveWidgets.reduce((max, widget) => Math.max(max, widget.y + widget.height), 8),
    [effectiveWidgets]
  );

  const finishGesture = useCallback(() => {
    const gesture = gestureRef.current;
    gestureRef.current = null;

    if (gesture && gesture.latest) {
      onLayoutChange(
        widgets.map((widget) =>
          widget.id === gesture.widget.id ? { ...widget, ...gesture.latest } : widget
        )
      );
    }
    setDraft(null);
  }, [widgets, onLayoutChange]);

  useEffect(() => {
    if (!gestureRef.current) return undefined;

    const handleMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      const stepX = columnWidth + GRID_GAP;
      const stepY = ROW_HEIGHT + GRID_GAP;
      const deltaColumns = Math.round((event.clientX - gesture.startX) / stepX);
      const deltaRows = Math.round((event.clientY - gesture.startY) / stepY);

      let next;
      if (gesture.mode === 'move') {
        next = {
          id: gesture.widget.id,
          x: clamp(gesture.widget.x + deltaColumns, 0, GRID_COLUMNS - gesture.widget.width),
          y: Math.max(0, gesture.widget.y + deltaRows),
          width: gesture.widget.width,
          height: gesture.widget.height,
        };
      } else {
        const width = clamp(
          gesture.widget.width + deltaColumns,
          MIN_WIDTH,
          GRID_COLUMNS - gesture.widget.x
        );
        next = {
          id: gesture.widget.id,
          x: gesture.widget.x,
          y: gesture.widget.y,
          width,
          height: Math.max(MIN_HEIGHT, gesture.widget.height + deltaRows),
        };
      }

      gesture.latest = next;
      setDraft(next);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', finishGesture);
    window.addEventListener('pointercancel', finishGesture);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', finishGesture);
      window.removeEventListener('pointercancel', finishGesture);
    };
  }, [columnWidth, finishGesture, draft]);

  const beginGesture = (mode, widget) => (event) => {
    // Ignore secondary buttons so right-click menus still work.
    if (event.button !== 0) return;
    event.preventDefault();
    gestureRef.current = {
      mode,
      widget,
      startX: event.clientX,
      startY: event.clientY,
      latest: null,
    };
    setDraft({ ...widget });
  };

  /** Keyboard equivalents, so the layout is adjustable without a pointer. */
  const handleKeyDown = (widget) => (event) => {
    const moves = {
      ArrowLeft: { x: -1 },
      ArrowRight: { x: 1 },
      ArrowUp: { y: -1 },
      ArrowDown: { y: 1 },
    };
    const resizes = {
      ArrowLeft: { width: -1 },
      ArrowRight: { width: 1 },
      ArrowUp: { height: -1 },
      ArrowDown: { height: 1 },
    };

    const table = event.shiftKey ? resizes : moves;
    const delta = table[event.key];
    if (!delta) return;

    event.preventDefault();
    const width = clamp(widget.width + (delta.width || 0), MIN_WIDTH, GRID_COLUMNS - widget.x);
    const next = {
      ...widget,
      width,
      height: Math.max(MIN_HEIGHT, widget.height + (delta.height || 0)),
      x: clamp(widget.x + (delta.x || 0), 0, GRID_COLUMNS - width),
      y: Math.max(0, widget.y + (delta.y || 0)),
    };

    onLayoutChange(widgets.map((item) => (item.id === widget.id ? next : item)));
  };

  return (
    <div
      className="lawn-grid"
      ref={containerRef}
      style={{
        gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
        gridAutoRows: `${ROW_HEIGHT}px`,
        gap: `${GRID_GAP}px`,
        minHeight: gridRows * (ROW_HEIGHT + GRID_GAP),
      }}
    >
      {effectiveWidgets.map((widget) => {
        const isActive = draft?.id === widget.id;
        return (
          <section
            key={widget.id}
            className={`lawn-widget${isActive ? ' lawn-widget-active' : ''}`}
            style={{
              gridColumn: `${widget.x + 1} / span ${widget.width}`,
              gridRow: `${widget.y + 1} / span ${widget.height}`,
            }}
          >
            <header
              className="lawn-widget-header"
              onPointerDown={beginGesture('move', widget)}
              onKeyDown={handleKeyDown(widget)}
              tabIndex={0}
              role="button"
              aria-label={`${widget.title || 'Widget'} — arrow keys move, shift plus arrows resize`}
            >
              <h2>{widget.title}</h2>
              <div className="lawn-widget-header-actions">
                {widget.headerAccessory}
                <button
                  type="button"
                  className="lawn-widget-remove"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={() => onRemoveWidget(widget.id)}
                  aria-label={`Remove ${widget.title} widget`}
                  title="Remove widget"
                >
                  <i className="ri-close-line" aria-hidden="true"></i>
                </button>
              </div>
            </header>

            <div className="lawn-widget-body">{renderWidget(widget)}</div>

            <span
              className="lawn-widget-resize"
              onPointerDown={beginGesture('resize', widget)}
              role="presentation"
              title="Drag to resize"
            />
          </section>
        );
      })}
    </div>
  );
}

WidgetGrid.propTypes = {
  widgets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      widgetType: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      title: PropTypes.string,
      headerAccessory: PropTypes.node,
    })
  ).isRequired,
  renderWidget: PropTypes.func.isRequired,
  onLayoutChange: PropTypes.func.isRequired,
  onRemoveWidget: PropTypes.func.isRequired,
};
